import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Spinner, Table, Badge, Form, InputGroup, Modal, Button as BootstrapButton } from 'react-bootstrap';
import ShopifyHeader from '../ShopifyHeader';
import ProductsSubMenu from './ProductsSubMenu';

const MeliProducts = () => {
    const { shop: shopParam } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showSyncDialog, setShowSyncDialog] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncMessage, setSyncMessage] = useState('');
    const [syncSuccess, setSyncSuccess] = useState(false);
    const [pagination, setPagination] = useState({
        totalCount: 0,
        limit: 50,
        offset: 0,
        hasMore: false
    });

    const shop = shopParam ? `${shopParam}.myshopify.com` : null;

    // Helper function to extract merchant name from shop domain
    const getMerchantName = (shopDomain) => {
        if (!shopDomain) return null;
        return shopDomain.replace('.myshopify.com', '');
    };

    // Helper function to load MELI products
    const loadMeliProducts = async (offset = 0, limit = 50) => {
        try {
            setIsLoading(true);
            setError('');

            const merchant = getMerchantName(shop);
            if (!merchant) {
                setError('Invalid shop domain. Please reinstall the app.');
                setIsLoading(false);
                return;
            }

            console.log(`Loading MELI products for merchant: ${merchant}, offset: ${offset}, limit: ${limit}`);

            const response = await fetch(`https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/shopify/meli_products?merchant=${merchant}&limit=${limit}&offset=${offset}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            console.log('MELI products API response:', result);

            if (result.success) {
                setProducts(result.data.products);
                setPagination({
                    totalCount: result.data.total_count,
                    limit: result.data.limit,
                    offset: result.data.offset,
                    hasMore: result.data.has_more
                });
            } else {
                throw new Error(result.error || 'Failed to load MELI products');
            }

        } catch (error) {
            console.error('Error loading MELI products:', error);
            setError(error.message || 'Failed to load MELI products');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!shop) {
            setError('Missing shop parameter. Please reinstall the app.');
            setIsLoading(false);
            return;
        }
        loadMeliProducts();
    }, [shop]); // eslint-disable-line react-hooks/exhaustive-deps

    // Helper function to render MELI listing badges with status and links
    const renderMeliListings = (meliListings, meliStatuses, meli_permalinks) => {
        const countries = [
            { key: 'mla', statusKey: 'mla_status', name: 'Argentina', color: 'primary' },
            { key: 'mlb', statusKey: 'mlb_status', name: 'Brazil', color: 'success' },
            { key: 'mlc', statusKey: 'mlc_status', name: 'Chile', color: 'info' },
            { key: 'mco', statusKey: 'mco_status', name: 'Colombia', color: 'warning' },
            { key: 'mlm', statusKey: 'mlm_status', name: 'Mexico', color: 'secondary' }
        ];

        return countries.map(country => {
            const listingId = meliListings[country.key];
            if (!listingId) return null;

            // Get status and permalink
            const status = meliStatuses[country.statusKey];
            const permalink = meli_permalinks && meli_permalinks[country.key];

            // Format status display
            let statusDisplay = '';
            if (status) {
                // Extract base status (remove sub-status part)
                const baseStatus = status.split('-')[0]; // eslint-disable-line no-unused-vars
                statusDisplay = ` - ${status}`;
            }

            return (
                <div key={country.key} className="d-inline-block me-1 mb-1">
                    {permalink ? (
                        <a
                            href={permalink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                        >
                            <Badge bg={country.color} className="cursor-pointer">
                                {country.name}{statusDisplay} <i className="fas fa-external-link-alt ms-1"></i>
                            </Badge>
                        </a>
                    ) : (
                        <Badge bg={country.color}>
                            {country.name}{statusDisplay}
                        </Badge>
                    )}
                </div>
            );
        }).filter(Boolean);
    };


    // Helper function to render pricing information
    const renderPricing = (meliPrices) => {// eslint-disable-line no-unused-vars
        const countries = [
            { key: 'ar', name: 'AR', symbol: '$' },
            { key: 'mx', name: 'MX', symbol: '$' },
            { key: 'br', name: 'BR', symbol: 'R$' },
            { key: 'cl', name: 'CL', symbol: '$' },
            { key: 'co', name: 'CO', symbol: '$' }
        ];

        return countries.map(country => {
            const price = meliPrices[country.key];
            if (!price) return null;

            return (
                <div key={country.key} className="d-flex justify-content-between">
                    <span>{country.name}:</span>
                    <span className="fw-bold">{country.symbol}{price.toFixed(2)}</span>
                </div>
            );
        }).filter(Boolean);
    };

    // Helper function to render shipping fees
    const renderShippingFees = (shippingFees) => { // eslint-disable-line no-unused-vars
        const countries = [
            { key: 'ar', name: 'AR', symbol: '$' },
            { key: 'mx', name: 'MX', symbol: '$' },
            { key: 'br', name: 'BR', symbol: 'R$' },
            { key: 'cl', name: 'CL', symbol: '$' },
            { key: 'co', name: 'CO', symbol: '$' }
        ];

        return countries.map(country => {
            const fee = shippingFees[country.key];
            if (!fee) return null;

            return (
                <div key={country.key} className="d-flex justify-content-between">
                    <span>{country.name}:</span>
                    <span className="fw-bold">{country.symbol}{fee.toFixed(2)}</span>
                </div>
            );
        }).filter(Boolean);
    };

    // Helper function to filter products by search term only
    const filterProducts = (products) => {
        if (!searchTerm) return products;

        return products.filter(product => {
            return (product.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (product.category || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (product.sku || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (product.brand || '').toLowerCase().includes(searchTerm.toLowerCase());
        });
    };

    const handleSyncAll = () => {
        setShowSyncDialog(true);
    };

    const handleConfirmSync = async () => {
        try {
            setIsSyncing(true);
            setSyncMessage('Starting sync...');
            setSyncSuccess(false);

            // Get shop domain from URL or sessionStorage
            const shopDomain = shopParam || sessionStorage.getItem('shopify_shop');

            if (!shopDomain) {
                setError('No shop domain found. Please refresh the page.');
                setSyncMessage('Error: No shop domain found');
                return;
            }

            // Hide product list during sync
            setProducts([]);
            setIsLoading(true);

            // TODO: Implement MELI sync API call
            // const response = await fetch('MELI_SYNC_ENDPOINT', { ... });

            // Simulate API call for now
            await new Promise(resolve => setTimeout(resolve, 2000));

            setSyncMessage('MELI sync completed successfully!');
            setSyncSuccess(true);

            // Refresh the MELI products list
            await loadMeliProducts();

        } catch (error) {
            console.error('MELI sync error:', error);
            setSyncMessage(`MELI sync failed: ${error.message}`);
            setSyncSuccess(false);
            setError(`MELI sync failed: ${error.message}`);
        } finally {
            setIsSyncing(false);
        }
    };

    const handleCancelSync = () => {
        setShowSyncDialog(false);
        setSyncMessage('');
        setSyncSuccess(false);
    };

    const handleLoadMore = () => {
        const newOffset = pagination.offset + pagination.limit;
        loadMeliProducts(newOffset, pagination.limit);
    };

    const filteredProducts = filterProducts(products);

    if (error) {
        return (
            <div style={{ minHeight: '100vh' }}>
                <ShopifyHeader />
                <Container className="py-5">
                    <Row className="justify-content-center">
                        <Col md={6}>
                            <Alert variant="danger">
                                <Alert.Heading>Error Loading MELI Products</Alert.Heading>
                                <p>{error}</p>
                                <Button variant="outline-danger" onClick={() => loadMeliProducts()}>
                                    Try Again
                                </Button>
                            </Alert>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh' }}>
            <ShopifyHeader />

            <Container className="py-4">
                {/* Products Sub Menu */}
                <ProductsSubMenu
                    activeTab="meli"
                    onTabChange={(tab) => {
                        if (tab === 'shopify') {
                            window.location.href = `/shopify/${shopParam}/products`;
                        }
                    }}
                />

                {/* Page Header */}
                <Row className="mb-4">
                    <Col>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h2>MELI Products</h2>
                                <small className="text-muted">
                                    Showing {filteredProducts.length} of {pagination.totalCount} products
                                </small>
                            </div>
                            <Button
                                variant="success"
                                onClick={handleSyncAll}
                                disabled={isLoading || isSyncing}
                                className="me-2"
                            >
                                {isSyncing ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Syncing...
                                    </>
                                ) : (
                                    'Sync All Products'
                                )}
                            </Button>
                        </div>
                    </Col>
                </Row>

                {/* Search Only */}
                <Row className="mb-4">
                    <Col md={6}>
                        <InputGroup>
                            <InputGroup.Text>
                                <i className="fas fa-search"></i>
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Search MELI products..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                </Row>

                {/* Products Table */}
                <Card>
                    <Card.Body className="p-0">
                        {isLoading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="success" />
                                <p className="mt-3 text-muted">Loading MELI products...</p>
                            </div>
                        ) : filteredProducts.length === 0 ? (
                            <div className="text-center py-5">
                                <i className="fas fa-box-open fa-3x text-muted mb-3"></i>
                                <h5>No MELI products</h5>
                                <p className="text-muted">
                                    {searchTerm
                                        ? 'Try adjusting your search term'
                                        : 'No Products published to MELI'
                                    }
                                </p>
                            </div>
                        ) : (
                            <Table responsive hover className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: '50%' }}>Product</th>
                                        <th style={{ width: '50%' }}>MELI Listings</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredProducts.map((product) => (
                                        <tr key={product.id}>
                                            {/* Product Column */}
                                            <td>
                                                <div className="d-flex align-items-start">
                                                    {product.images && product.images.length > 0 && (
                                                        <img
                                                            src={product.images[0]}
                                                            alt={product.title}
                                                            className="me-3"
                                                            style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }}
                                                        />
                                                    )}
                                                    <div className="flex-grow-1">
                                                        <h6 className="mb-1">{String(product.title || 'Untitled Product')}</h6>
                                                        <p className="text-muted mb-2 small">
                                                            {product.description || 'No description available'}
                                                        </p>
                                                        <div className="mb-2">
                                                            <Badge bg="secondary" className="me-1">SKU: {product.sku || 'N/A'}</Badge>
                                                            <Badge bg="info" className="me-1">UPC: {product.upc || 'N/A'}</Badge>
                                                            {product.brand && <Badge bg="primary">{product.brand}</Badge>}
                                                        </div>
                                                        <div className="mb-2">
                                                            <small className="text-muted">
                                                                Category: {product.category || 'Uncategorized'} <br />
                                                                MELI Category: {product.meli_category_name || 'N/A'} <br />
                                                                MELI Domain: {product.meli_category_domain_name || 'N/A'} <br />
                                                                Stock: {product.stock || 0} -
                                                                Weight: {product.shipping_weight_lb || 'N/A'} lbs
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* MELI Listings Column */}
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <div className="mb-2">
                                                        <div className="mt-1">
                                                            {renderMeliListings(product.meli_listings, product.meli_statuses, product.meli_permalinks)}
                                                        </div>
                                                    </div>

                                                    {product.published_to_meli && (
                                                        <div className="mb-2">
                                                            <small className="text-success">
                                                                <i className="fas fa-check-circle me-1"></i>
                                                                Published {new Date(product.published_to_meli).toLocaleDateString()}
                                                            </small>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Pricing & Status Column */}
                                            <td>

                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>

                {/* Load More Button */}
                {pagination.hasMore && !isLoading && (
                    <div className="text-center mt-4">
                        <Button variant="outline-primary" onClick={handleLoadMore}>
                            Load More Products
                        </Button>
                    </div>
                )}
            </Container>

            {/* Sync Confirmation Dialog */}
            <Modal show={showSyncDialog} onHide={handleCancelSync} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Sync All MELI Products</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!isSyncing && !syncMessage ? (
                        <>
                            <p>Are you sure you want to sync all MELI products?</p>
                            <p className="text-muted">
                                This will fetch all products from MELI and update the database.
                            </p>
                            <div className="alert alert-info">
                                <strong>Note:</strong> This operation may take a few minutes depending on the number of products.
                            </div>
                        </>
                    ) : (
                        <div className="text-center">
                            {isSyncing ? (
                                <>
                                    <Spinner animation="border" variant="success" className="mb-3" />
                                    <p className="mb-0">{syncMessage}</p>
                                </>
                            ) : (
                                <>
                                    <div className={`alert ${syncSuccess ? 'alert-success' : 'alert-danger'} mb-3`}>
                                        <i className={`fas ${syncSuccess ? 'fa-check-circle' : 'fa-exclamation-circle'} me-2`}></i>
                                        {syncMessage}
                                    </div>
                                    {syncSuccess && (
                                        <p className="text-muted">
                                            MELI products have been refreshed and are now up to date.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <BootstrapButton
                        variant="secondary"
                        onClick={handleCancelSync}
                        disabled={isSyncing}
                    >
                        {syncSuccess ? 'Close' : 'Cancel'}
                    </BootstrapButton>
                    {!syncSuccess && (
                        <BootstrapButton
                            variant="success"
                            onClick={handleConfirmSync}
                            disabled={isSyncing}
                        >
                            {isSyncing ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Syncing...
                                </>
                            ) : (
                                'Sync Now'
                            )}
                        </BootstrapButton>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default MeliProducts;


